import { User, Mail, Briefcase, RefreshCw } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { useAccountStore } from '@/stores/accountStore'
import styles from '@/styles/Dashboard.module.css'

// Function to get a random color class - same as in dashboard.tsx
const getRandomColorClass = () => {
  const colorClasses = [
    'color-red',
    'color-blue',
    'color-green',
    'color-purple',
    'color-orange',
    'color-pink',
    'color-teal',
    'color-yellow',
  ]
  const randomIndex = Math.floor(Math.random() * colorClasses.length)
  return colorClasses[randomIndex]
}

export function AccountSettings() {
  const { toast } = useToast()
  const { 
    id, 
    email, 
    name, 
    role, 
    title, 
    primaryOrganization, 
    organizations, 
    files,
    isLoading, 
    error,
    fetchAccountInfo, 
    updateAccountInfo 
  } = useAccountStore()
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    title: ''
  })
  
  // Create random color classes for each card
  const [accountColorClass] = useState(getRandomColorClass)
  const [orgColorClass] = useState(getRandomColorClass)
  const [filesColorClass] = useState(getRandomColorClass)

  // Load account data on component mount
  useEffect(() => {
    fetchAccountInfo()
  }, [fetchAccountInfo])

  // Update form when account data is loaded
  useEffect(() => {
    if (name || role || title) {
      setFormData({
        name: name || '',
        role: role || '',
        title: title || ''
      })
    }
  }, [name, role, title])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate form data
    if (!formData.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please provide your name",
        variant: "destructive"
      })
      return
    }

    try {
      await updateAccountInfo(formData)
      toast({
        title: "Account Updated",
        description: "Your account information has been updated successfully."
      })
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error.message || "An error occurred while updating your account",
        variant: "destructive"
      })
    }
  }

  if (isLoading && !name) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading account information...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center p-6 text-red-500">
        <p>Failed to load account information: {error}</p>
        <Button 
          variant="outline" 
          onClick={fetchAccountInfo} 
          className="mt-4"
        >
          <RefreshCw className="mr-2 h-4 w-4" /> Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className={`space-y-8 ${styles.accountSettingsContainer}`}>
      <div className={`${styles.project_card_wrapper} ${styles[accountColorClass]}`}>
        <Card 
          className={`${styles.accountCard} ${styles.projectCardAnimated} ${styles.colorful}`}
        >
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="mr-2 h-5 w-5" /> Account Information
            </CardTitle>
            <CardDescription>
              Update your personal information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  value={email || ''}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Email cannot be changed
                </p>
              </div>
              
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="e.g., Developer, Manager, etc."
                  />
                </div>
                <div>
                  <Label htmlFor="title">Job Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Software Engineer, Project Manager, etc."
                  />
                </div>
              </div>
              
              <div className="pt-4">
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> 
                      Updating...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      <div className={`${styles.project_card_wrapper} ${styles[orgColorClass]}`}>
        <Card 
          className={`${styles.accountCard} ${styles.projectCardAnimated} ${styles.colorful}`}
        >
          <CardHeader>
            <CardTitle className="flex items-center">
              <Briefcase className="mr-2 h-5 w-5" /> Organization
            </CardTitle>
            <CardDescription>
              Your organization information
            </CardDescription>
          </CardHeader>
          <CardContent>
            {primaryOrganization ? (
              <div className="space-y-2">
                <p className="text-lg font-medium">{primaryOrganization.name}</p>
                <p className="text-sm text-gray-500">
                  You're a member of {organizations?.length || 0} organization{(organizations?.length || 0) !== 1 ? 's' : ''}
                </p>
                <p className="text-sm">
                  Manage your organizations in the Organizations tab
                </p>
              </div>
            ) : (
              <p>You aren't a member of any organization yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      <div className={`${styles.project_card_wrapper} ${styles[filesColorClass]}`}>
        <Card 
          className={`${styles.accountCard} ${styles.projectCardAnimated} ${styles.colorful}`}
        >
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="mr-2 h-5 w-5" /> Recent Files
            </CardTitle>
            <CardDescription>
              Your recently uploaded files
            </CardDescription>
          </CardHeader>
          <CardContent>
            {files && files.length > 0 ? (
              <ul className="space-y-2">
                {files.map((file) => (
                  <li key={file.id} className="text-sm">
                    <a 
                      href={file.url} 
                      target="_blank" 
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {file.filename}
                    </a>
                    <span className="text-gray-500 ml-2">
                      ({Math.round(file.size / 1024)} KB)
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>You haven't uploaded any files yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}